/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import logo from "@assets/svg/logo/qr-logo.svg";
import chFlag from "@assets/svg/swiss-qr-flag.svg";
import { TalerUri, TalerUris, TranslatedString } from "@gnu-taler/taler-util";
import { h, VNode } from "preact";
import qrcode from "qrcode-generator";

function generate_qr(
  text: string,
  params: {
    typeNumber?: TypeNumber;
    errorCorrectionLevel?: ErrorCorrectionLevel;
  } = {},
) {
  const qr = qrcode(
    params.typeNumber ?? 0,
    (params.errorCorrectionLevel = "H"),
  );
  qr.addData(text, "Byte");
  qr.make();
  const image = qr.createSvgTag({
    scalable: true,
    margin: 0,
  });
  return `data:image/svg+xml,${encodeURIComponent(image)}`;
}

export function QR_Taler({ uri }: { uri: TalerUri }): VNode {
  const stringUri = TalerUris.toString(uri);
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        maxWidth: 400,
        margin: "auto",
        padding: 10, // size of the animated border
        borderRadius: 20,
        background: `conic-gradient(
        from var(--angle), 
        #0042B3  0deg,
        #F1F1F4  20deg,
        #F1F1F4 150deg,
        #F1F1F4 160deg,
        #0042B3 180deg,
        #F1F1F4 200deg,
        #F1F1F4 330deg,
        #F1F1F4 340deg,
        #0042B3) padding-box`,
        animation: "rotate 10s linear infinite",
        position: "relative",
      }}
    >
      <div
        style={{
          padding: 10, // separate the qr from the animated border
          borderRadius: 20, // match the radius from the parent
          backgroundColor: "white", // hide the background
        }}
      >
        <img style={{ margin: 5 }} src={generate_qr(stringUri)} />
      </div>
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          alignContent: "center",
          display: "flex",
          backgroundColor: "transparent", // section 19.90.4.3
        }}
      >
        <img src={logo} style={{ width: 100, height: 50 }} />
      </div>
    </div>
  );
}

export function QR_TOTP({ otpAuthURI }: { otpAuthURI: string }): VNode {
  return (
    <div
      style={{
        width: "90%",
        maxWidth: 400,
        margin: "auto",
        padding: 10,
        position: "relative",
      }}
    >
      <div
        style={{
          padding: 10, // separate the qr from the animated border
          borderRadius: 20, // match the radius from the parent
          backgroundColor: "white", // hide the background
        }}
      >
        <img style={{ margin: 5 }} src={generate_qr(otpAuthURI)} />
      </div>

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          alignContent: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            border: "1px solid black",
            backgroundColor: "white",
            fontWeight: "bold",
            padding: 5,
          }}
        >
          T-OTP
        </div>
      </div>
    </div>
  );
}

export function QR_Generic({
  content,
  title,
}: {
  content: string;
  title?: string;
}): VNode {
  return (
    <div
      style={{
        width: "90%",
        maxWidth: 400,
        margin: "auto",
        padding: 10,
        position: "relative",
      }}
    >
      <div
        style={{
          padding: 10, // separate the qr from the animated border
          borderRadius: 20, // match the radius from the parent
          backgroundColor: "white", // hide the background
        }}
      >
        <img style={{ margin: 5 }} src={generate_qr(content)} />
      </div>

      {title === undefined ? undefined : (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            alignContent: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              border: "1px solid black",
              backgroundColor: "white",
              fontWeight: "bold",
              padding: 5,
            }}
          >
            {title}
          </div>
        </div>
      )}
    </div>
  );
}
/**
 * Based on the definition of Swiss Implementation Guidelines
 * for the QR-bill
 * @param param0
 * @returns
 */
export function QR_SwissBank({ text }: { text: string }): VNode {
  return (
    <div
      style={{
        width: "90%",
        maxWidth: 300,
        margin: "auto",
        padding: 10,
        position: "relative",
      }}
    >
      <div
        style={{
          padding: 10, // separate the qr from the animated border
          borderRadius: 20, // match the radius from the parent
          backgroundColor: "white", // hide the background
        }}
      >
        <img
          style={{ margin: 5 }}
          src={generate_qr(text, {
            typeNumber: 0, // section 6.2
            errorCorrectionLevel: "M", // section 6.1
          })}
        />
      </div>

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          alignContent: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            backgroundColor: "white",
          }}
        >
          <img src={chFlag} style={{ width: 50, height: 50 }} />
        </div>
      </div>
    </div>
  );
}

export function QR_Bank({
  text,
  label,
}: {
  text: string;
  label: TranslatedString;
}): VNode {
  return (
    <div
      style={{
        width: "90%",
        maxWidth: 300,
        margin: "auto",
        position: "relative",
      }}
    >
      <div
        style={{
          padding: 10, // separate the qr from the animated border
          borderRadius: 20, // match the radius from the parent
          backgroundColor: "white", // hide the background
        }}
      >
        <img style={{ margin: 5 }} src={generate_qr(text)} />
      </div>

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          alignContent: "center",
        }}
      >
        <div
          style={{ fontWeight: "bold", backgroundColor: "white", padding: 3 }}
        >
          {label}
        </div>
      </div>
    </div>
  );
}
